'use client';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useQuery } from '@parkchirp/api-client';
import { FC, useCallback, useState } from 'react';
import { GET_SUBLOTS } from '../../queries';
import type { ExportAsCsv, SetShowSnackbar } from '../../types';
import { dayjs } from '../../utils';
import { ReportActionButton } from './reportActionButton';

interface SignUpTerminationReportProps {
	facilityId: string;
	exportAsCsv: ExportAsCsv;
	setShowSnackbar: SetShowSnackbar;
	accountId?: number;
	tenantSubscriptionId?: string;
}

export const SignUpTerminationReportForm: FC<SignUpTerminationReportProps> = ({
	facilityId,
	accountId,
	exportAsCsv,
	setShowSnackbar,
	tenantSubscriptionId,
}) => {
	const [parkingStartDate, setParkingStartDate] = useState<dayjs.Dayjs | null>(null);
	const [parkingEndDate, setParkingEndDate] = useState<dayjs.Dayjs | null>(null);
	const [transactionStartDate, setTransactionStartDate] = useState<dayjs.Dayjs | null>(null);
	const [transactionEndDate, setTransactionEndDate] = useState<dayjs.Dayjs | null>(null);
	const [isDownloadingCsv, setIsDownloadingCsv] = useState<boolean>(false);
	const [selectedSublotId, setSelectedSublotId] = useState<null | number>(null);

	const { data: sublotsData } = useQuery(GET_SUBLOTS, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		variables: {
			facilityId,
			includePrimary: true,
		},
		skip: !facilityId,
		fetchPolicy: 'network-only',
	});

	const handleValidation = useCallback(() => {
		const validations = [
			{
				message: `Parking start date should be before parking end date.`,
				failingCondition: parkingStartDate && parkingEndDate && parkingStartDate.isAfter(parkingEndDate),
			},
			{
				message: `Transaction start date should be before transaction end date.`,
				failingCondition:
					transactionStartDate && transactionEndDate && transactionStartDate.isAfter(transactionEndDate),
			},
		];
		const failingValidations = validations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}
		return true;
	}, [parkingStartDate, parkingEndDate, transactionStartDate, transactionEndDate, setShowSnackbar]);

	const getArgs = () => ({
		tenantSubscriptionId,
		sublotId: selectedSublotId,
		filters: [
			{
				model: 'BILLING_PROFILE',
				field: 'billing_profile.facility_id',
				op: 'EQUALS',
				value: facilityId,
			},
			{
				model: 'PARKER',
				field: 'parker.termination_status',
				op: 'NOT_EQUALS',
				value: 'Pending',
			},
			...(accountId
				? [
						{
							model: 'SUBSCRIPTION',
							field: 'subscription.account_id',
							op: 'EQUALS',
							value: accountId,
						},
					]
				: []),
			...(parkingEndDate
				? [
						{
							model: 'PARKER',
							field: 'parker_start_date',
							op: 'LT_EQUALS',
							value: parkingEndDate.format('YYYY-MM-DD'),
						},
					]
				: []),
			...(parkingStartDate
				? [
						{
							model: 'PARKER',
							field: 'parker_start_date',
							op: 'GT_EQUALS',
							value: parkingStartDate.format('YYYY-MM-DD'),
						},
					]
				: []),
		],
		orFilters: [
			[
				...(transactionEndDate
					? [
							{
								model: 'PARKER',
								field: 'parker.created_at',
								op: 'LT_EQUALS',
								value: transactionEndDate.format('YYYY-MM-DD'),
							},
						]
					: []),
				...(transactionStartDate
					? [
							{
								model: 'PARKER',
								field: 'parker.created_at',
								op: 'GT_EQUALS',
								value: transactionStartDate.format('YYYY-MM-DD'),
							},
						]
					: []),
			],
			[
				...(transactionEndDate
					? [
							{
								model: 'PARKER',
								field: 'nearest_termination_date',
								op: 'LT_EQUALS',
								value: transactionEndDate.format('YYYY-MM-DD'),
							},
						]
					: []),
				...(transactionStartDate
					? [
							{
								model: 'PARKER',
								field: 'nearest_termination_date',
								op: 'GT_EQUALS',
								value: transactionStartDate.format('YYYY-MM-DD'),
							},
						]
					: []),
			],
		].filter((filters) => filters.length),
	});

	const handleReset = useCallback(() => {
		setParkingStartDate(null);
		setParkingEndDate(null);
		setTransactionStartDate(null);
		setTransactionEndDate(null);
		setSelectedSublotId(null);
	}, []);

	return (
		<Box sx={{ padding: 3 }}>
			<Grid container spacing={2} direction='row'>
				<Grid item xs={6}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Parker Start Date'
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'parker-start-date-picker' },
								},
							}}
							value={parkingStartDate}
							onChange={setParkingStartDate}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={6}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Transaction Start Date'
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'transaction-start-date-picker' },
								},
							}}
							value={transactionStartDate}
							onChange={setTransactionStartDate}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={6}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Parker End Date'
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'parker-end-date-picker' },
								},
							}}
							value={parkingEndDate}
							onChange={setParkingEndDate}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={6}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Transaction End Date'
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'transaction-end-date-picker' },
								},
							}}
							value={transactionEndDate}
							onChange={setTransactionEndDate}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={6}>
					<FormControl fullWidth variant='outlined'>
						<InputLabel>Sublot</InputLabel>
						<Select
							label='Sublot'
							value={selectedSublotId || ''}
							onChange={(e) => {
								const subId = Number(e.target.value);
								setSelectedSublotId(subId);
							}}
						>
							{sublotsData?.getSublots?.map((sublot, index) => (
								<MenuItem key={index} value={sublot.id}>
									{sublot.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>

			<ReportActionButton
				showPdfButton={false}
				downloadReport={() =>
					exportAsCsv({
						params: getArgs(),
						handleValidation,
						setIsDownloadingCsv,
						reportName: 'signup_termination_report',
					})
				}
				isDownloadingPdf={false}
				isDownloadingCsv={isDownloadingCsv}
				disableDownloadButtons={!facilityId}
				onReset={handleReset}
			/>
		</Box>
	);
};
